/**
 * AgentSettingsPanel — Settings > AI Assistant
 *
 * Three jobs:
 *   1. Show WHICH model is answering and WHERE it runs (local vs cloud), so
 *      an operator can tell at a glance whether deal data is leaving the box.
 *   2. Let an admin manage what the assistant has been taught — view, add,
 *      and remove terms without going through chat.
 *   3. Explain, in the UI, what the assistant is allowed to do — because the
 *      write-confirmation guarantee is the thing people need to trust.
 *
 * Model selection is deliberately READ-ONLY here. AGENT_MODEL and the API key
 * are backend environment variables; exposing a model switcher in the browser
 * would imply the frontend can change them, which it cannot (and shouldn't —
 * that would be a way to redirect where merchant data is sent).
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Bot, RefreshCw, Trash2, Plus, ShieldCheck, Cloud, HardDrive,
  AlertCircle, BookOpen, Pencil, ScrollText, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { tokenStorage } from '../../lib/tokenStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AgentSettingsPanel = () => {
  const [health, setHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(true);
  const [terms, setTerms] = useState([]);
  const [loadingTerms, setLoadingTerms] = useState(true);
  const [newTerm, setNewTerm] = useState('');
  const [newDefinition, setNewDefinition] = useState('');
  const [saving, setSaving] = useState(false);

  // Assistant name + shortcut prompts
  const [assistantName, setAssistantName] = useState('');
  const [shortcuts, setShortcuts] = useState([]);
  const [savingConfig, setSavingConfig] = useState(false);

  // Activity log
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${tokenStorage.getToken()}` } }),
    []
  );

  const loadHealth = useCallback(async () => {
    setLoadingHealth(true);
    try {
      const r = await axios.get(`${API}/agent/health`, authHeaders());
      setHealth(r.data);
    } catch (e) {
      setHealth({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setLoadingHealth(false);
    }
  }, [authHeaders]);

  const loadTerms = useCallback(async () => {
    setLoadingTerms(true);
    try {
      const r = await axios.get(`${API}/agent/memory`, authHeaders());
      setTerms(r.data?.terms || []);
    } catch {
      setTerms([]);
    } finally {
      setLoadingTerms(false);
    }
  }, [authHeaders]);

  const loadConfig = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/agent/config`, authHeaders());
      setAssistantName(r.data?.assistant_name || '');
      setShortcuts(r.data?.shortcuts || []);
    } catch {
      /* leave defaults */
    }
  }, [authHeaders]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const r = await axios.get(`${API}/agent/history?limit=100`, authHeaders());
      setHistory(r.data?.entries || []);
    } catch {
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    loadHealth();
    loadTerms();
    loadConfig();
    loadHistory();
  }, [loadHealth, loadTerms, loadConfig, loadHistory]);

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const r = await axios.put(
        `${API}/agent/config`,
        {
          assistant_name: assistantName,
          shortcuts: shortcuts.map((s) => s.trim()).filter(Boolean),
        },
        authHeaders()
      );
      setAssistantName(r.data?.assistant_name || '');
      setShortcuts(r.data?.shortcuts || []);
      toast.success('Assistant settings saved. Reload to see them in the panel.');
    } catch (e) {
      toast.error(`Could not save: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSavingConfig(false);
    }
  };

  const resetConfig = async () => {
    setSavingConfig(true);
    try {
      const r = await axios.post(`${API}/agent/config/reset`, {}, authHeaders());
      setAssistantName(r.data?.assistant_name || '');
      setShortcuts(r.data?.shortcuts || []);
      toast.success('Restored the built-in name and shortcuts.');
    } catch (e) {
      toast.error(`Could not reset: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSavingConfig(false);
    }
  };

  const setShortcutAt = (i, val) =>
    setShortcuts((s) => s.map((v, idx) => (idx === i ? val : v)));
  const removeShortcutAt = (i) =>
    setShortcuts((s) => s.filter((_, idx) => idx !== i));
  const addShortcut = () =>
    setShortcuts((s) => (s.length >= 8 ? s : [...s, '']));

  const addTerm = async () => {
    const term = newTerm.trim();
    const definition = newDefinition.trim();
    if (!term || !definition) {
      toast.error('Both the term and what it means are required.');
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/agent/memory`, { term, definition }, authHeaders()
      );
      toast.success(
        r.data?.action === 'reinforced'
          ? `Reinforced "${term}" (confirmed ${r.data.times_confirmed}x)`
          : `Learned "${term}"`
      );
      setNewTerm('');
      setNewDefinition('');
      loadTerms();
    } catch (e) {
      toast.error(`Could not save: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const removeTerm = async (term) => {
    try {
      await axios.delete(
        `${API}/agent/memory/${encodeURIComponent(term)}`, authHeaders()
      );
      toast.success(`Removed "${term}"`);
      loadTerms();
    } catch (e) {
      toast.error(`Could not remove: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const activeTerms = terms.filter((t) => t.active !== false);
  const isLocal = !!health?.local;

  return (
    <div className="space-y-6">
      {/* ── Status ─────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5" /> Assistant Status
              </CardTitle>
              <CardDescription>
                Which model answers, and where it runs.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadHealth}
              disabled={loadingHealth}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loadingHealth ? 'animate-spin' : ''}`} />
              Re-check
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <p className="text-xs text-slate-500 mb-1">Status</p>
              {loadingHealth ? (
                <Badge variant="secondary">checking…</Badge>
              ) : health?.ok ? (
                <Badge className="bg-emerald-600 hover:bg-emerald-600">Online</Badge>
              ) : (
                <Badge variant="destructive">Offline</Badge>
              )}
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Model</p>
              <p className="text-sm font-medium text-slate-900">
                {health?.model_label || health?.model || '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Runs</p>
              <p className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                {isLocal
                  ? <><HardDrive className="h-4 w-4 text-emerald-600" /> On this machine</>
                  : <><Cloud className="h-4 w-4 text-blue-600" /> Model provider</>}
              </p>
            </div>
          </div>

          {!health?.ok && health?.error && (
            <div className="flex gap-2 text-sm bg-red-50 border border-red-200 text-red-800
                            rounded-md p-3">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span className="break-words">{health.error}</span>
            </div>
          )}

          <div className={`text-xs rounded-md p-3 border ${
            isLocal
              ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
              : 'bg-blue-50 border-blue-200 text-blue-900'
          }`}>
            {isLocal
              ? 'Inference happens on this machine. No deal data leaves the network.'
              : 'Deal data included in a conversation — merchant names, balances, '
                + 'payment history — is sent to the model provider for processing.'}
          </div>

          <p className="text-xs text-slate-500">
            The model is set by the <code className="text-slate-700">AGENT_MODEL</code>{' '}
            environment variable on the backend and cannot be changed from the browser.
          </p>
        </CardContent>
      </Card>

      {/* ── Name + shortcuts ───────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5" /> Name &amp; Shortcuts
          </CardTitle>
          <CardDescription>
            What the assistant is called, and the one-click prompts shown when
            the chat panel is empty.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="max-w-sm">
            <Label htmlFor="assistant-name" className="text-xs">Assistant name</Label>
            <Input
              id="assistant-name"
              value={assistantName}
              onChange={(e) => setAssistantName(e.target.value)}
              placeholder="Assistant"
              maxLength={40}
            />
            <p className="text-xs text-slate-500 mt-1">
              Shown in the chat header. The assistant will also use this name if
              asked who it is.
            </p>
          </div>

          <div>
            <Label className="text-xs">Shortcut prompts</Label>
            <p className="text-xs text-slate-500 mb-2">
              Up to 8. Leave empty to hide the buttons entirely.
            </p>
            <div className="space-y-2">
              {shortcuts.map((s, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    value={s}
                    onChange={(e) => setShortcutAt(i, e.target.value)}
                    placeholder="e.g. 10879 sent zelle for 300"
                    maxLength={120}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeShortcutAt(i)}
                    className="text-slate-400 hover:text-red-600 shrink-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {shortcuts.length === 0 && (
                <p className="text-xs text-slate-400 italic">
                  No shortcuts — the panel will show none.
                </p>
              )}
            </div>
            {shortcuts.length < 8 && (
              <Button variant="outline" size="sm" onClick={addShortcut} className="mt-2">
                <Plus className="h-4 w-4 mr-1" /> Add shortcut
              </Button>
            )}
          </div>

          <div className="flex gap-2 pt-1">
            <Button onClick={saveConfig} disabled={savingConfig}>Save</Button>
            <Button variant="outline" onClick={resetConfig} disabled={savingConfig}>
              Reset to defaults
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── What it's allowed to do ────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" /> Permissions
          </CardTitle>
          <CardDescription>What the assistant can and cannot do.</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="text-sm text-slate-700 space-y-2">
            <li className="flex gap-2">
              <span className="text-emerald-600 font-bold">✓</span>
              Looks up deals, balances, transactions and refinance eligibility
              automatically.
            </li>
            <li className="flex gap-2">
              <span className="text-emerald-600 font-bold">✓</span>
              Drafts refinances and payments, and shows a full breakdown first.
            </li>
            <li className="flex gap-2">
              <span className="text-amber-600 font-bold">!</span>
              <span>
                <strong>Never writes without confirmation.</strong> Creating a deal,
                posting a refinance payoff or a payment only happens after an admin
                confirms the breakdown.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-emerald-600 font-bold">✓</span>
              Balance figures come from the locked payoff formula, not from the
              model's own arithmetic.
            </li>
            <li className="flex gap-2">
              <span className="text-slate-400 font-bold">×</span>
              Admin only. Brokers and syndicators cannot see or use it.
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* ── Teach it ───────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" /> What it has learned
          </CardTitle>
          <CardDescription>
            Terms and procedures you've taught it. These are added to every
            conversation, so it remembers them permanently — no retraining.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Add form */}
          <div className="grid gap-3 sm:grid-cols-[220px_1fr_auto] sm:items-end">
            <div>
              <Label htmlFor="agent-term" className="text-xs">Term or shorthand</Label>
              <Input
                id="agent-term"
                value={newTerm}
                onChange={(e) => setNewTerm(e.target.value)}
                placeholder="stip"
              />
            </div>
            <div>
              <Label htmlFor="agent-def" className="text-xs">What it means / what to do</Label>
              <Textarea
                id="agent-def"
                rows={1}
                value={newDefinition}
                onChange={(e) => setNewDefinition(e.target.value)}
                placeholder="Stipulation — a document required before funding."
                className="min-h-[38px]"
              />
            </div>
            <Button onClick={addTerm} disabled={saving}>
              <Plus className="h-4 w-4 mr-1" /> Teach
            </Button>
          </div>

          {/* List */}
          {loadingTerms ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : activeTerms.length === 0 ? (
            <div className="text-center py-8 border border-dashed rounded-lg">
              <BookOpen className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-600">Nothing taught yet.</p>
              <p className="text-xs text-slate-400 mt-1">
                Core MCA terminology (RTR, factor rate, refi, EPO) is already built in.
              </p>
            </div>
          ) : (
            <div className="border rounded-lg divide-y">
              {activeTerms.map((t) => (
                <div key={t.id || t.term} className="flex items-start gap-3 p-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900">{t.term}</span>
                      {t.promoted && (
                        <Badge variant="secondary" className="text-[10px]">trusted</Badge>
                      )}
                      <span className="text-[11px] text-slate-400">
                        confirmed {t.times_confirmed || 1}×
                        {typeof t.confidence_score === 'number'
                          && ` · ${Math.round(t.confidence_score)}%`}
                        {t.updated_at && ` · ${new Date(t.updated_at)
                          .toLocaleDateString('en-US')}`}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-0.5 break-words">
                      {t.definition}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeTerm(t.term)}
                    className="text-slate-400 hover:text-red-600 shrink-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-slate-500">
            You can also teach it mid-conversation — just tell it what something
            means and it will store it. Repeating a term raises its confidence;
            at 3 confirmations it's marked trusted. Core terminology lives in{' '}
            <code className="text-slate-700">backend/services/agent/glossary.py</code>.
          </p>
        </CardContent>
      </Card>

      {/* ── Activity log ───────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ScrollText className="h-5 w-5" /> Activity Log
              </CardTitle>
              <CardDescription>
                Who said what to the assistant, and whether it posted a write.
                Newest first.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={loadHistory}
                    disabled={loadingHistory}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loadingHistory ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingHistory ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : history.length === 0 ? (
            <div className="text-center py-8 border border-dashed rounded-lg">
              <ScrollText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-600">No activity yet.</p>
            </div>
          ) : (
            <div className="border rounded-lg divide-y max-h-[420px] overflow-y-auto">
              {history.map((h, i) => (
                <div key={h.id || i} className="flex items-start gap-3 p-3">
                  <div className="mt-0.5 shrink-0">
                    {h.posted ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <Bot className="h-4 w-4 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap text-[11px] text-slate-400">
                      <span className="font-medium text-slate-600">
                        {h.user_email || 'unknown'}
                      </span>
                      {h.posted && (
                        <Badge className="bg-emerald-600 hover:bg-emerald-600 text-[10px]">
                          posted
                        </Badge>
                      )}
                      {h.action && !h.posted && (
                        <Badge variant="secondary" className="text-[10px]">
                          {h.action}
                        </Badge>
                      )}
                      {h.created_at && (
                        <span>{new Date(h.created_at).toLocaleString('en-US')}</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-800 mt-0.5 break-words">
                      {h.message}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-slate-500 mt-3">
            Stored in the <code className="text-slate-700">agent_audit</code>{' '}
            collection. Green check = a write was posted.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AgentSettingsPanel;
